/**
 * Redesigned AuditSessionsFilters Component
 *
 * Provides comprehensive filtering and sorting functionality for audit sessions.
 * Features a compact main bar with expandable filter card for detailed filtering.
 */

import { useState, useCallback, useMemo } from 'react';

import * as styles from './AuditSessionsFilters.css';
import { SessionsFilterActions } from './SessionsFilterActions';
import { SessionsFilterBar } from './SessionsFilterBar';
import { SessionsFilterGrid } from './SessionsFilterGrid';
import { hasActiveFilters, getActiveFilterCount } from './helpers';
import type { AuditSessionsFiltersProps } from './types';
import { createEmptyFilters } from './types';

export function AuditSessionsFilters({
  filters,
  onFiltersChange,
  sort,
  onSortChange,
  pageSize,
  onPageSizeChange,
  filterOptions,
  isLoading = false,
}: AuditSessionsFiltersProps) {
  // UI state for filter expansion
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);

  // Check if any filters are active
  const filtersActive = useMemo(() => hasActiveFilters(filters), [filters]);

  // Count active filters for display
  const activeFilterCount = useMemo(() => getActiveFilterCount(filters), [filters]);

  // Handle filter toggle - simplified without animation state tracking
  const handleFilterToggle = useCallback(() => {
    setIsFilterExpanded(prev => !prev);
  }, []);

  // Handle clear all filters
  const handleClearAllFilters = useCallback(() => {
    onFiltersChange(createEmptyFilters());
  }, [onFiltersChange]);

  return (
    <div className={styles.filtersContainer} data-loading={isLoading}>
      {/* Main Filter Bar */}
      <SessionsFilterBar
        activeFilterCount={activeFilterCount}
        filtersActive={filtersActive}
        isFilterExpanded={isFilterExpanded}
        isLoading={isLoading}
        pageSize={pageSize}
        sort={sort}
        onFilterToggle={handleFilterToggle}
        onPageSizeChange={onPageSizeChange}
        onSortChange={onSortChange}
      />

      {/* Expandable Filter Card */}
      <div className={styles.filterCardContainer} data-expanded={isFilterExpanded}>
        <SessionsFilterGrid filterOptions={filterOptions} filters={filters} isLoading={isLoading} onFiltersChange={onFiltersChange} />

        <SessionsFilterActions
          activeFilterCount={activeFilterCount}
          filtersActive={filtersActive}
          isLoading={isLoading}
          onClearAllFilters={handleClearAllFilters}
        />
      </div>
    </div>
  );
}
